import React, { useEffect, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { api } from '@/lib/api';
import { Ban, Plus, X, Save, Smartphone, Hash } from 'lucide-react';
import { toast } from 'sonner';

export default function AdminBlocklist() {
    const [mobiles, setMobiles] = useState([]);
    const [inAppIds, setInAppIds] = useState([]);
    const [mobileInput, setMobileInput] = useState('');
    const [idInput, setIdInput] = useState('');
    const [saving, setSaving] = useState(false);

    const load = () => api.get('/admin/blocklist').then(r => { setMobiles(r.data.mobiles || []); setInAppIds(r.data.in_app_ids || []); }).catch(() => {});
    useEffect(() => { load(); }, []);

    const save = async (m, ids) => {
        setSaving(true);
        try {
            const { data } = await api.put('/admin/blocklist', { mobiles: m, in_app_ids: ids });
            setMobiles(data.mobiles || []); setInAppIds(data.in_app_ids || []);
            toast.success('Blocklist updated');
        } catch (e) { toast.error(e.response?.data?.detail || 'Could not save'); }
        finally { setSaving(false); }
    };

    const addMobile = () => {
        const v = mobileInput.trim();
        if (!v) return;
        if (mobiles.includes(v)) { toast.message('Already blocked'); setMobileInput(''); return; }
        save([...mobiles, v], inAppIds); setMobileInput('');
    };
    const addId = () => {
        const v = idInput.trim();
        if (!v) return;
        if (inAppIds.includes(v)) { toast.message('Already blocked'); setIdInput(''); return; }
        save(mobiles, [...inAppIds, v]); setIdInput('');
    };
    const removeMobile = (v) => save(mobiles.filter(x => x !== v), inAppIds);
    const removeId = (v) => save(mobiles, inAppIds.filter(x => x !== v));

    return (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card className="p-4 sm:p-5" data-testid="blocklist-mobiles-card">
                <div className="flex items-center gap-2 font-display font-semibold"><Smartphone className="h-4 w-4 text-rose-600" /> Blocked WhatsApp / mobile numbers</div>
                <p className="text-xs text-muted-foreground mt-1">These numbers cannot create a new account on the website.</p>
                <div className="mt-3 flex gap-2">
                    <Input value={mobileInput} onChange={e => setMobileInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && addMobile()} placeholder="e.g. 9876543210" data-testid="blocklist-mobile-input" />
                    <Button onClick={addMobile} disabled={saving} className="bg-[#0E2A47] hover:bg-[#0B223A]" data-testid="blocklist-mobile-add"><Plus className="h-4 w-4" /></Button>
                </div>
                <div className="mt-3 flex flex-wrap gap-2" data-testid="blocklist-mobiles-list">
                    {mobiles.map(m => (
                        <span key={m} className="inline-flex items-center gap-1 rounded-full bg-rose-50 border border-rose-200 text-rose-800 px-2.5 py-1 text-sm font-mono" data-testid={`blocklist-mobile-${m}`}>
                            {m}<button onClick={() => removeMobile(m)} className="hover:text-rose-950" data-testid={`blocklist-mobile-remove-${m}`}><X className="h-3.5 w-3.5" /></button>
                        </span>
                    ))}
                    {mobiles.length === 0 && <span className="text-sm text-muted-foreground">No numbers blocked.</span>}
                </div>
            </Card>

            <Card className="p-4 sm:p-5" data-testid="blocklist-ids-card">
                <div className="flex items-center gap-2 font-display font-semibold"><Hash className="h-4 w-4 text-rose-600" /> Blocked in-app IDs</div>
                <p className="text-xs text-muted-foreground mt-1">These in-app IDs cannot receive recharges from the website (user recharges only).</p>
                <div className="mt-3 flex gap-2">
                    <Input value={idInput} onChange={e => setIdInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && addId()} placeholder="e.g. 123456789" data-testid="blocklist-id-input" />
                    <Button onClick={addId} disabled={saving} className="bg-[#0E2A47] hover:bg-[#0B223A]" data-testid="blocklist-id-add"><Plus className="h-4 w-4" /></Button>
                </div>
                <div className="mt-3 flex flex-wrap gap-2" data-testid="blocklist-ids-list">
                    {inAppIds.map(i => (
                        <span key={i} className="inline-flex items-center gap-1 rounded-full bg-rose-50 border border-rose-200 text-rose-800 px-2.5 py-1 text-sm font-mono" data-testid={`blocklist-id-${i}`}>
                            {i}<button onClick={() => removeId(i)} className="hover:text-rose-950" data-testid={`blocklist-id-remove-${i}`}><X className="h-3.5 w-3.5" /></button>
                        </span>
                    ))}
                    {inAppIds.length === 0 && <span className="text-sm text-muted-foreground">No IDs blocked.</span>}
                </div>
            </Card>
        </div>
    );
}
